import { DailyRoutine } from './application/daily-routine.ts';
import { openDailyRoutine, LabFaults } from './infrastructure/daily-routine-idb.ts';
import type { DailyRoutineCheck } from './domain/daily-routine.ts';
import { openTrainingCatalog, TrainingLabFaults } from './infrastructure/training-idb.ts';
import { TrainingExercises } from './application/training-exercises.ts';
import { TrainingRoutines } from './application/training-routines.ts';
import { TrainingPlanning } from './application/training-planning.ts';
import { openTrainingPlanning } from './infrastructure/training-planning-idb.ts';
import { openTrainingSessions, SessionLabFaults } from './infrastructure/training-session-idb.ts';
import { TrainingSessions } from './application/training-sessions.ts';
import { TrainingHistory } from './application/training-history.ts';
import { openNutrition, NutritionLabFaults } from './infrastructure/nutrition-idb.ts';
import { NutritionRecipes } from './application/nutrition-recipes.ts';
import { NutritionPlanning } from './application/nutrition-planning.ts';
import { NutritionConsumption } from './application/nutrition-consumption.ts';
import { NutritionShopping } from './application/nutrition-shopping.ts';
import { openProgress, ProgressLabFaults } from './infrastructure/progress-idb.ts';
import { ProgressWeight } from './application/progress-weight.ts';
import { ProgressMetrics } from './application/progress-metrics.ts';
import { PublicTrainingProgress } from './application/training-progress-source.ts';
import { PublicNutritionProgress } from './application/nutrition-progress-source.ts';
import { HomeDashboard } from './application/home-dashboard.ts';
import { PublicTrainingHome } from './application/training-home-source.ts';
import { PublicNutritionHome } from './application/nutrition-home-source.ts';
import { PublicProgressHome } from './application/progress-home-source.ts';
import { PublicTrainingNutrition } from './application/training-nutrition-source.ts';
import { BackupExport } from './application/backup-export.ts';
import { openBackupRepository } from './infrastructure/backup-idb.ts';
import { RestoreManager } from './application/restore.ts';
import type { RestoreRecoveryOutcome, RestoreResult } from './domain/restore.ts';
import { openRestoreRepository, RestoreLabFaults } from './infrastructure/restore-idb.ts';

export const fixture: DailyRoutineCheck = {
  checkId: '30000000-0000-4000-8000-000000000001',
  itemId: '20000000-0000-4000-8000-000000000001',
  localDate: '2026-09-11', label: 'Ítem sintético FÉNIX', marked: false, revision: 1,
  recordedInstant: '2026-09-11T10:00:00.000Z', zoneId: 'Europe/Madrid', utcOffsetMinutes: 120,
};
export const datasetId = '10000000-0000-4000-8000-000000000001';
export const databaseName = 'fniz-g4-synthetic-v1';
export async function compose() {
  const restoreFaults =
    new RestoreLabFaults();

  const restoreRepository =
    await openRestoreRepository(
      databaseName,
      restoreFaults,
    );

  const restore =
    restoreRepository.ok
      ? new RestoreManager(
        restoreRepository.value,
        {
          operationId: () =>
            crypto.randomUUID(),

          datasetId: () =>
            crypto.randomUUID(),

          now: () =>
            new Date().toISOString(),
        },
      )
      : null;

  const restoreRecovery:
    RestoreResult<RestoreRecoveryOutcome> =
    restore
      ? await restore.recover()
      : {
          ok: false,
          code:
            restoreRepository.ok
              ? 'STORAGE_UNAVAILABLE'
              : restoreRepository.code,
        };

  const faults = new LabFaults();
  const repository = await openDailyRoutine(databaseName, fixture, datasetId, faults);
  const port = repository.ok ? repository.value : {
    readCheck: async () => repository, setCheck: async () => repository,
  };
  const app = new DailyRoutine(port, {
    operationId: () => crypto.randomUUID(),
    time: () => { const now = new Date(); return {
      recordedInstant: now.toISOString(), zoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
      utcOffsetMinutes: -now.getTimezoneOffset(),
    }; },
  });
  const trainingFaults = new TrainingLabFaults();
  const catalog = await openTrainingCatalog(databaseName, trainingFaults);
  const planningFaults = new TrainingLabFaults();
  const planningRepository = await openTrainingPlanning(databaseName, planningFaults);
  const sessionFaults = new SessionLabFaults();
  const sessions = await openTrainingSessions(databaseName, sessionFaults);
  const training = catalog.ok && planningRepository.ok && sessions.ok ? {
    exercises: new TrainingExercises(catalog.value, () => crypto.randomUUID()),
    routines: new TrainingRoutines(catalog.value, () => crypto.randomUUID()),
    planning: new TrainingPlanning(planningRepository.value, catalog.value, () => crypto.randomUUID(), sessions.value),
    sessions: new TrainingSessions(sessions.value, planningRepository.value, catalog.value, () => crypto.randomUUID(), () => new Date().toISOString()),
    history: new TrainingHistory(sessions.value, catalog.value),
  } : null;
  const nutritionFaults=new NutritionLabFaults(),nutritionRepository=await openNutrition(databaseName,nutritionFaults);
  const trainingNutrition = catalog.ok && planningRepository.ok && sessions.ok
    ? new PublicTrainingNutrition(planningRepository.value, catalog.value, sessions.value)
    : null;
  const nutrition=nutritionRepository.ok?{
    recipes:new NutritionRecipes(nutritionRepository.value,()=>crypto.randomUUID()),
    planning:new NutritionPlanning(nutritionRepository.value,()=>crypto.randomUUID()),
    consumption:new NutritionConsumption(nutritionRepository.value,()=>crypto.randomUUID()),
    shopping:new NutritionShopping(nutritionRepository.value,()=>crypto.randomUUID()),training:trainingNutrition,
  }:null;
  const progressFaults=new ProgressLabFaults(),progressRepository=await openProgress(databaseName,progressFaults);
  const progress=progressRepository.ok&&sessions.ok&&catalog.ok&&nutritionRepository.ok?{
    weight:new ProgressWeight(progressRepository.value,()=>crypto.randomUUID()),
    metrics:new ProgressMetrics(new PublicTrainingProgress(sessions.value,catalog.value),new PublicNutritionProgress(nutritionRepository.value)),
  }:null;
  const homeTraining = planningRepository.ok && catalog.ok && sessions.ok
    ? new PublicTrainingHome(planningRepository.value, catalog.value, sessions.value)
    : null;
  const homeNutrition = nutritionRepository.ok
    ? new PublicNutritionHome(nutritionRepository.value)
    : null;
  const homeProgress = progressRepository.ok
    ? new PublicProgressHome(progressRepository.value)
    : null;
  const home = homeTraining && homeNutrition && homeProgress
    ? new HomeDashboard(homeTraining, homeNutrition, homeProgress)
    : null;
  const backupRepository =
    await openBackupRepository(
      databaseName,
    );

  const backup =
    backupRepository.ok
      ? new BackupExport(
        backupRepository.value,
        {
          producerAppVersion:
            '0.1.0-g6.8',

          backupId: () =>
            crypto.randomUUID(),

          now: () => {
            const now =
              new Date();

            return {
              exportedAt:
                now.toISOString(),

              timeContext: {
                zoneId:
                  Intl.DateTimeFormat()
                    .resolvedOptions()
                    .timeZone,

                utcOffsetMinutes:
                  -now.getTimezoneOffset(),
              },
            };
          },
        },
      )
      : null;

  return {
    app,
    repository,
    faults,
    training,
    nutrition,
    progress,
    home,
    backup,
    restore,
    restoreRepository,
    restoreRecovery,
    restoreFaults,
    progressFaults,
    trainingFaults,
    planningFaults,
    sessionFaults,
    nutritionFaults,
  };
}
